import { Row, Col, Card, Spin, Space, Tag, Tabs, Table, Button, Tooltip } from "antd";
import React, { useState, useEffect } from "react";
import { Link, useParams, useLocation } from "react-router-dom";
import useRequest from "../../hooks/useRequest";
import { ShowToast, Severty } from "../../helper/toast";
import apiPath from "../../constants/apiPath";
import moment from "moment";
import { ArrowLeftIcon, EyeIcon } from "lucide-react";
import SubscriptionPurchaseModal from "../../components/SubscriptionPurchaseModal";
const { TabPane } = Tabs;

const STATUS_COLORS = {
  created: "gold",
  paid: "green",
  failed: "red",
  refunded: "default",
};

const SUB_STATUS_COLORS = { active: "green", upcoming: "gold", expired: "default", cancelled: "red" };

function View() {

  const sectionName = "Transaction";
  const routeName = "transaction";

  const params = useParams();
  const location = useLocation();
  const { request } = useRequest();
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(false);
  const [selectedSub, setSelectedSub] = useState(null);
  const [subModalOpen, setSubModalOpen] = useState(false);

  const fetchData = (id) => {
    setLoading(true);
    request({
      url: `${apiPath.viewTransaction}/${id}`,
      method: "GET",
      onSuccess: (response) => {
        setData(response.data || {});
        setLoading(false);
      },
      onError: (error) => {
        setLoading(false);
        ShowToast(error, Severty.ERROR);
      },
    });
  };

  useEffect(() => {
    fetchData(params.id);
  }, []);

  const fmtDate = (v) => (v ? moment(v).format("DD-MMM-YYYY HH:mm") : "-");
  const fmtAmount = (v) => `₹${v ? Number(v).toLocaleString("en-IN") : 0}`;

  const segmentNames = Array.isArray(data?.segment_ids)
    ? data.segment_ids.map((s) => (s && s.en_name ? s.en_name : "")).filter(Boolean).join(", ")
    : "";

  const subscriptions = Array.isArray(data?.related_subscriptions) ? data.related_subscriptions : [];

  const subscriptionColumns = [
    { title: "Plan", key: "en_name", render: (_, r) => <span className="cap">{r.en_name || "-"}</span> },
    { title: "Segment", key: "segment", render: (_, r) => (r.segment_id?.en_name || r.segment_en_name || "-") },
    { title: "Duration", key: "selected_duration", render: (_, r) => (r.selected_duration ? `${r.selected_duration} Month${r.selected_duration > 1 ? "s" : ""}` : "-") },
    { title: "Amount Paid", key: "amount_paid", render: (_, r) => fmtAmount(r.amount_paid) },
    { title: "Status", key: "status", render: (_, r) => <Tag color={SUB_STATUS_COLORS[r.status] || "default"} className="cap">{r.status || "-"}</Tag> },
    { title: "Valid From", key: "start_date", render: (_, r) => fmtDate(r.start_date) },
    { title: "Valid Till", key: "end_date", render: (_, r) => fmtDate(r.end_date) },
    {
      title: "Action",
      key: "action",
      render: (_, r) => (
        <Tooltip title="View purchase details" color="purple">
          <Button onClick={() => { setSelectedSub(r); setSubModalOpen(true); }}>
            <EyeIcon size={15} />
          </Button>
        </Tooltip>
      ),
    },
  ];

  return (
    <Spin spinning={loading} size="large">
      <Tabs defaultActiveKey="transaction">

        {/* ---- Transaction details ---- */}
        <TabPane tab="Transaction" key="transaction">
          <Card title={`${sectionName} Details`} variant={false} extra={<Tag color={STATUS_COLORS[data?.status] || "default"} className="cap">{data?.status || "-"}</Tag>}>
            <Row gutter={[24, 16]}>
              <Col span={24} md={12}>
                <Card title="Payment" type="inner">
                  <Space direction="vertical" size="middle" style={{ width: "100%" }}>
                    <div className="view-inner-cls"><h5>Amount:</h5><h6>{fmtAmount(data.amount)}</h6></div>
                    <div className="view-inner-cls"><h5>Currency:</h5><h6>{data.currency || "INR"}</h6></div>
                    <div className="view-inner-cls"><h5>Duration:</h5><h6>{data.duration ? `${data.duration} Month${data.duration > 1 ? "s" : ""}` : "-"}</h6></div>
                    <div className="view-inner-cls"><h5>Status:</h5><h6><Tag color={STATUS_COLORS[data?.status] || "default"} className="cap">{data?.status || "-"}</Tag></h6></div>
                    <div className="view-inner-cls"><h5>Paid At:</h5><h6>{fmtDate(data.paid_at)}</h6></div>
                  </Space>
                </Card>
              </Col>

              <Col span={24} md={12}>
                <Card title="Razorpay" type="inner">
                  <Space direction="vertical" size="middle" style={{ width: "100%" }}>
                    <div className="view-inner-cls"><h5>Order ID:</h5><h6 style={{ wordBreak: "break-all" }}>{data.razorpay_order_id || "-"}</h6></div>
                    <div className="view-inner-cls"><h5>Payment ID:</h5><h6 style={{ wordBreak: "break-all" }}>{data.razorpay_payment_id || "-"}</h6></div>
                    <div className="view-inner-cls"><h5>Signature:</h5><h6 style={{ wordBreak: "break-all" }}>{data.razorpay_signature || "-"}</h6></div>
                    <div className="view-inner-cls"><h5>Payment Method:</h5><h6>{data.payment_method || "-"}</h6></div>
                  </Space>
                </Card>
              </Col>

              <Col span={24} md={12}>
                <Card title="User" type="inner">
                  <Space direction="vertical" size="middle" style={{ width: "100%" }}>
                    <div className="view-inner-cls"><h5>Name:</h5><h6>{data.user_name || "-"}</h6></div>
                    <div className="view-inner-cls"><h5>Phone:</h5><h6>{data.user_phone || "-"}</h6></div>
                    <div className="view-inner-cls"><h5>Email:</h5><h6>{data.user_email || "-"}</h6></div>
                  </Space>
                </Card>
              </Col>

              <Col span={24} md={12}>
                <Card title="Plan" type="inner">
                  <Space direction="vertical" size="middle" style={{ width: "100%" }}>
                    <div className="view-inner-cls"><h5>Plan Name:</h5><h6>{data.plan_name || "-"}</h6></div>
                    <div className="view-inner-cls"><h5>Plan Type:</h5><h6 className="cap">{data.plan_type || "-"}</h6></div>
                    <div className="view-inner-cls"><h5>Segment(s):</h5><h6>{segmentNames || "-"}</h6></div>
                  </Space>
                </Card>
              </Col>

              <Col span={24} md={12}>
                <Card title="Refund" type="inner">
                  <Space direction="vertical" size="middle" style={{ width: "100%" }}>
                    <div className="view-inner-cls"><h5>Refund ID:</h5><h6 style={{ wordBreak: "break-all" }}>{data.refund_id || "-"}</h6></div>
                    <div className="view-inner-cls"><h5>Refund Amount:</h5><h6>{data.refund_amount != null ? fmtAmount(data.refund_amount) : "-"}</h6></div>
                    <div className="view-inner-cls"><h5>Refund Reason:</h5><h6>{data.refund_reason || "-"}</h6></div>
                    <div className="view-inner-cls"><h5>Refunded At:</h5><h6>{fmtDate(data.refunded_at)}</h6></div>
                  </Space>
                </Card>
              </Col>

              <Col span={24} md={12}>
                <Card title="Meta" type="inner">
                  <Space direction="vertical" size="middle" style={{ width: "100%" }}>
                    <div className="view-inner-cls"><h5>Record ID:</h5><h6 style={{ wordBreak: "break-all" }}>{data._id || "-"}</h6></div>
                    <div className="view-inner-cls"><h5>Created At:</h5><h6>{fmtDate(data.created_at)}</h6></div>
                    <div className="view-inner-cls"><h5>Updated At:</h5><h6>{fmtDate(data.updated_at)}</h6></div>
                  </Space>
                </Card>
              </Col>
            </Row>
          </Card>
        </TabPane>

        {/* ---- Related purchased subscription(s) ---- */}
        <TabPane tab={`Purchased Subscription (${subscriptions.length})`} key="subscription">
          <Card variant={false}>
            <div className="table-responsive">
              <Table
                rowKey="_id"
                columns={subscriptionColumns}
                dataSource={subscriptions}
                pagination={false}
                className="ant-border-space"
              />
            </div>
          </Card>
        </TabPane>
      </Tabs>

      <div className="float-right mt-3">
        <Link className="ant-btn ant-btn-secondary" to={`/${routeName}${location.search}`}>
          <ArrowLeftIcon size={15} /> Back
        </Link>
      </div>

      <SubscriptionPurchaseModal
        open={subModalOpen}
        data={selectedSub}
        onClose={() => setSubModalOpen(false)}
      />
    </Spin>
  );
}

export default View;
